import React from "react";
import { Icon } from "antd";

/**
 * LocalStorage key that stores the maintenance document id the user dismissed.
 * Exported so the layout controller can compare against it.
 */
export const RELEASE_ANNOUNCEMENT_KEY = "status_maintenance_dismissed_id";

// Fallback "status portal" link used by the Learn More button when the Firestore
// document does not carry its own `link` field.
const STATUS_PORTAL_URL = process.env.REACT_APP_STATUS_PORTAL_URL || "";

const wrapperStyle = {
	width: "100%",
	background: "#fffbe6",
	borderBottom: "1px solid #ffe58f",
	color: "#614700",
	fontSize: 13,
	lineHeight: 1.4,
	padding: "8px 44px 8px 16px",
	position: "relative",
	display: "flex",
	alignItems: "center",
	flexWrap: "wrap",
	gap: 8,
};

const badgeStyle = {
	background: "#614700",
	color: "#fffbe6",
	fontSize: 11,
	fontWeight: 600,
	textTransform: "uppercase",
	letterSpacing: 0.4,
	padding: "2px 8px",
	borderRadius: 3,
	whiteSpace: "nowrap",
};

const learnMoreStyle = {
	color: "#614700",
	fontWeight: 600,
	textDecoration: "underline",
	cursor: "pointer",
	whiteSpace: "nowrap",
};

const closeStyle = {
	position: "absolute",
	right: 14,
	top: "50%",
	transform: "translateY(-50%)",
	cursor: "pointer",
	fontSize: 14,
	color: "#614700",
};

// Convert a server/UTC timestamp string into a human readable Eastern-time label,
// e.g. "September 10 at 10:30 AM EST". Uses the native Intl API so no extra
// timezone dependency is required (handles EST/EDT automatically).
const formatEastern = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (isNaN(date.getTime())) return value;

	try {
		const datePart = new Intl.DateTimeFormat("en-US", {
			timeZone: "America/New_York",
			month: "long",
			day: "numeric",
		}).format(date);
		const timePart = new Intl.DateTimeFormat("en-US", {
			timeZone: "America/New_York",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		}).format(date);
		return `${datePart} at ${timePart} EST`;
	} catch (e) {
		return date.toUTCString();
	}
};

const ReleaseAnnouncementBanner = ({ maintenanceData, onDismiss }) => {
	if (!maintenanceData) return null;

	const { id, title, startDate, link } = maintenanceData;
	const learnMoreLink = link || STATUS_PORTAL_URL;

	const handleClose = () => {
		try {
			localStorage.setItem(RELEASE_ANNOUNCEMENT_KEY, id || "");
		} catch (e) {
			/* ignore storage errors */
		}
		if (onDismiss) onDismiss();
	};

	return (
		<div style={wrapperStyle} role="alert">
			<Icon type="clock-circle" theme="filled" style={{ fontSize: 16, color: "#d48806" }} />
			<span style={badgeStyle}>System Status</span>
			<span style={{ fontWeight: 600 }}>{title || "Scheduled system maintenance"}</span>
			{startDate ? <span>Starting {formatEastern(startDate)}.</span> : null}
			{learnMoreLink ? (
				<a
					style={learnMoreStyle}
					href={learnMoreLink}
					target="_blank"
					rel="noopener noreferrer"
				>
					Learn More
				</a>
			) : null}
			<Icon type="close" style={closeStyle} onClick={handleClose} title="Dismiss" />
		</div>
	);
};

export default ReleaseAnnouncementBanner;
